if (!customElements.get('experience-upsell-recommendations')) {
  customElements.define(
    'experience-upsell-recommendations',
    class ExperienceUpsellRecommendations extends HTMLElement {
      connectedCallback() {
        this.handleToggle = this.handleToggle.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.handleKeyboardAddToCart = this.handleKeyboardAddToCart.bind(this);
        this.addEventListener('click', this.handleToggle);
        this.addEventListener('change', this.handleVariantChange);
        this.addEventListener('keydown', this.handleKeyboardAddToCart);

        if (this.dataset.loaded === 'true') {
          this.startHourlyRotation();
          return;
        }
        if (!this.dataset.url) return;
        this.loadRecommendations();
      }

      disconnectedCallback() {
        if (this.requestController) this.requestController.abort();
        if (this.requestTimeout) window.clearTimeout(this.requestTimeout);
        if (this.rotationTimer) window.clearTimeout(this.rotationTimer);
        this.removeEventListener('click', this.handleToggle);
        this.removeEventListener('change', this.handleVariantChange);
        this.removeEventListener('keydown', this.handleKeyboardAddToCart);
      }

      async loadRecommendations() {
        if (this.loading) return;
        this.loading = true;
        this.requestController = new AbortController();
        this.requestTimeout = window.setTimeout(() => this.requestController.abort(), 8000);

        try {
          const response = await fetch(this.dataset.url, { signal: this.requestController.signal });
          if (!response.ok) throw new Error(`Upsell recommendations request failed: ${response.status}`);

          const text = await response.text();
          const html = new DOMParser().parseFromString(text, 'text/html');
          const recommendations = html.querySelector('experience-upsell-recommendations');

          if (!recommendations || recommendations.dataset.loaded !== 'true' || !recommendations.innerHTML.trim()) {
            this.hidden = true;
            return;
          }

          this.innerHTML = recommendations.innerHTML;
          this.dataset.loaded = 'true';
          this.setAttribute('aria-busy', 'false');
          this.startHourlyRotation();
        } catch (error) {
          this.hidden = true;
          if (error.name !== 'AbortError') console.error(error);
        } finally {
          window.clearTimeout(this.requestTimeout);
          this.loading = false;
        }
      }

      startHourlyRotation() {
        if (this.rotationTimer) window.clearTimeout(this.rotationTimer);

        const savedState = this.getRotationState();
        this.rotationIndex = savedState.index;
        this.rotationDeadline = savedState.deadline;
        this.rotateRecommendations(false);
        this.scheduleNextRotation();
      }

      scheduleNextRotation() {
        const delay = Math.max(1000, this.rotationDeadline - Date.now());
        this.rotationTimer = window.setTimeout(() => {
          const now = Date.now();
          if (now >= this.rotationDeadline) {
            const elapsedRotations = Math.floor((now - this.rotationDeadline) / (60 * 60 * 1000)) + 1;
            this.rotationIndex += elapsedRotations;
            this.rotationDeadline += elapsedRotations * 60 * 60 * 1000;
            this.saveRotationState();
            this.rotateRecommendations(true);
          }
          this.scheduleNextRotation();
        }, delay);
      }

      getRotationState() {
        const interval = 60 * 60 * 1000;
        const now = Date.now();
        const fallback = { index: 0, deadline: now + interval };

        try {
          const storedState = JSON.parse(localStorage.getItem('experience-upsell-hourly-rotation-v1'));
          const indexIsValid = Number.isInteger(storedState?.index) && storedState.index >= 0;
          const deadlineIsValid = Number.isFinite(storedState?.deadline) && storedState.deadline > 0;

          if (!indexIsValid || !deadlineIsValid || storedState.deadline > now + interval) {
            localStorage.setItem('experience-upsell-hourly-rotation-v1', JSON.stringify(fallback));
            return fallback;
          }

          if (storedState.deadline <= now) {
            const elapsedRotations = Math.floor((now - storedState.deadline) / interval) + 1;
            storedState.index += elapsedRotations;
            storedState.deadline += elapsedRotations * interval;
            localStorage.setItem('experience-upsell-hourly-rotation-v1', JSON.stringify(storedState));
          }

          return storedState;
        } catch (error) {
          return fallback;
        }
      }

      saveRotationState() {
        try {
          localStorage.setItem(
            'experience-upsell-hourly-rotation-v1',
            JSON.stringify({ index: this.rotationIndex, deadline: this.rotationDeadline })
          );
        } catch (error) {
          // The live timer still works when browser storage is unavailable.
        }
      }

      rotateRecommendations(resetScroll) {
        const list = this.querySelector('.experience-upsell__list');
        const items = list ? [...list.children] : [];
        if (!items.length) return;

        const visibleCount = Math.min(5, items.length);
        const groupCount = Math.ceil(items.length / visibleCount);
        const startIndex = (this.rotationIndex % groupCount) * visibleCount;
        const visibleItems = new Set();

        for (let index = 0; index < visibleCount; index += 1) {
          visibleItems.add(items[(startIndex + index) % items.length]);
        }

        items.forEach((item) => {
          item.hidden = !visibleItems.has(item);
          if (item.hidden) {
            const details = item.querySelector('.experience-upsell__inline-details');
            if (details) details.hidden = true;
            item.querySelector('.experience-upsell__item')?.classList.remove('is-expanded');
            item.querySelectorAll('[data-upsell-toggle]').forEach((button) => {
              button.setAttribute('aria-expanded', 'false');
            });
          }
        });

        this.dataset.rotationIndex = String(this.rotationIndex % groupCount);
        this.dataset.rotationReady = 'true';
        if (resetScroll) list.scrollTo({ left: 0, behavior: 'auto' });
      }

      handleVariantChange(event) {
        const select = event.target.closest('[data-upsell-variant]');
        if (!select || !this.contains(select)) return;

        const option = select.options[select.selectedIndex];
        const item = select.closest('.experience-upsell__item');
        const image = item?.querySelector('.experience-upsell__image');
        const imageSrc = option?.dataset.imageSrc;
        if (!image || !imageSrc || image.dataset.variantImage === imageSrc) return;

        const selectedVariantId = select.value;
        const preloadedImage = new Image();
        const applyImage = () => {
          if (select.value !== selectedVariantId) return;

          image.src = imageSrc;
          if (option.dataset.imageSrcset) image.srcset = option.dataset.imageSrcset;
          if (option.dataset.imageAlt) image.alt = option.dataset.imageAlt;
          image.dataset.variantImage = imageSrc;
        };

        preloadedImage.onload = applyImage;
        preloadedImage.src = imageSrc;
        if (preloadedImage.complete) applyImage();
      }

      handleToggle(event) {
        const trigger = event.target.closest('[data-upsell-toggle]');
        if (!trigger || !this.contains(trigger)) return;

        const item = trigger.closest('.experience-upsell__item');
        const detailsId = trigger.getAttribute('aria-controls');
        const details = item?.querySelector(`[id="${detailsId}"]`);
        if (!item || !details) return;

        const shouldOpen = details.hidden;
        details.hidden = !shouldOpen;
        item.classList.toggle('is-expanded', shouldOpen);

        item.querySelectorAll(`[aria-controls="${detailsId}"]`).forEach((button) => {
          button.setAttribute('aria-expanded', String(shouldOpen));
        });
      }

      handleKeyboardAddToCart(event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        const addToCart = event.target.closest('add-to-cart');
        if (!addToCart) return;

        event.preventDefault();
        addToCart.click();
      }
    }
  );
}
